import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { LoadingSpinerArea, SetupMenu, SetupMenuItem } from '../../../../../components';
import { selectorUser, selectorUserLoadFinished } from '../../../../../lib/selectorUser';
import History from '../../../../../utils/History';
import { getProductAccess, hasResolvedAccessRule, IPA_ADDITIONAL_CONTRACTS, isProductRule } from '../../../../../utils/ipaAccess';
import { SetupCampaign } from '../SetupCampaign';
import { SetupFarma } from '../SetupFarma';
import { SetupIPACategoryLevels } from '../SetupIPACategoryLevels';
import { SetupIPADadosDeConcorrencia } from '../SetupIPADadosDeConcorrencia';
import { SetupIpaBasicConfig } from '../SetupIpaBasicConfig';
import { SetupIpaChannels } from '../SetupIpaChannels';
import { SetupIpaEngineUpdateNotifications } from '../SetupIpaEngineUpdateNotifications';
import { SetupIpaPricingResponsibilities } from '../SetupIpaPricingResponsibilities';
import { SetupIpaProductsSegmentation } from '../SetupIpaProductsSegmentation/SetupIpaProductsSegmentation';
import { SetupIpaStoresAndClusters } from '../SetupIpaStoresAndClusters/SetupIpaStoresAndClusters';
import { SetupSupplierNegotiationConfig } from '../SetupSupplierNegotiationConfig';
import SetupIpaPriceArchitecture from '../SetupIpaPriceArchitecture';
import { hasPriceArchitectureAccess, isSubmenuVisibleByProduct, PRICE_ARCHITECTURE_CONTRACT, SubmenuProductRule } from './menuVisibility';
import styles from './SetupIPA.module.scss';

const PREFIX_URL = '/administrador/produtos/IPA';

type ContractAdditionalType = string;

type MenuItemVisibilityContext = {
  user: ReturnType<typeof selectorUser>;
  contratosAdicionais: ContractAdditionalType[];
  productRule: boolean;
  hasPricing: boolean;
  hasNegotiation: boolean;
  clientHasNegotiation: boolean;
};

type MenuItem = {
  to: string;
  label: string;
  component: React.ComponentType;
  isVisible?: (context: MenuItemVisibilityContext) => boolean;
  productVisibility?: SubmenuProductRule;
  // Exigência de perfil que vale nas duas regras de acesso (por pacote e por produto).
  adminOnly?: boolean;
};

const DEFAULT_MENU_ITEM_VISIBILITY = () => true;

const isMenuItemVisible = (item: MenuItem, context: MenuItemVisibilityContext) => {
  if (item.adminOnly && !context.user?.usuarioAdministrador) return false;
  if (context.productRule) return item.productVisibility != null && isSubmenuVisibleByProduct(item.productVisibility, context);
  return (item.isVisible ?? DEFAULT_MENU_ITEM_VISIBILITY)(context);
};

const MENU_ITEMS: Record<string, MenuItem> = {
  'configuracoes-basicas': {
    to: 'configuracoes-basicas',
    label: 'Configurações Básicas',
    component: SetupIpaBasicConfig,
    productVisibility: { product: 'ANY' },
  },
  'dados-de-concorrencia': {
    to: 'dados-de-concorrencia',
    label: 'Dados de concorrência',
    component: SetupIPADadosDeConcorrencia,
    productVisibility: { product: 'ANY' },
  },
  'negociacao-com-fornecedores': {
    to: 'negociacao-com-fornecedores',
    label: 'Negociação com fornecedores',
    component: SetupSupplierNegotiationConfig,
    isVisible: ({ contratosAdicionais }) => contratosAdicionais.includes('MODULO_NEGOCIACAO'),
    // Grava em /setup/negotiation-cost, que o gateway trata como rota de Precificacao.
    productVisibility: { product: 'PRICING', requiresClientNegotiation: true },
  },
  // Desabilitado temporariamente
  // A api não está funcionando

  // 'dados-do-infopanel': {
  //     to: 'dados-do-infopanel',
  //     label: 'Dados do InfoPanel',
  //     component: SetupIPAForm,
  // },
  'segmentacao-de-produtos': {
    to: 'segmentacao-de-produtos',
    label: 'Segmentação de Produtos',
    component: SetupIpaProductsSegmentation,
    productVisibility: { product: 'PRICING' },
  },
  'niveis-de-categoria': {
    to: 'niveis-de-categoria',
    label: 'Níveis de Categoria',
    component: SetupIPACategoryLevels,
    productVisibility: { product: 'PRICING' },
  },
  'responsaveis-pela-precificacao': {
    to: 'responsaveis-pela-precificacao',
    label: 'Responsáveis pela precificação',
    component: SetupIpaPricingResponsibilities,
    productVisibility: { product: 'PRICING' },
  },
  'lojas-e-clusters': {
    to: 'lojas-e-clusters',
    label: 'Lojas e Clusters',
    component: SetupIpaStoresAndClusters,
    productVisibility: { product: 'ANY' },
  },
  'canais-de-venda': {
    to: 'canais-de-venda',
    label: 'Canais de Venda',
    component: SetupIpaChannels,
    productVisibility: { product: 'PRICING' },
  },
  farma: {
    to: 'farma',
    label: 'Farma',
    component: SetupFarma,
    productVisibility: { product: 'PRICING' },
  },
  campanhas: {
    to: 'campanhas',
    label: 'Campanhas promocionais',
    component: SetupCampaign,
    isVisible: ({ contratosAdicionais }) => contratosAdicionais.includes('CAMPANHAS_PROMOCIONAIS'),
    productVisibility: { product: 'PRICING', additional: IPA_ADDITIONAL_CONTRACTS.CAMPAIGNS },
  },
  'arquitetura-de-precos': {
    to: 'arquitetura-de-precos',
    label: 'Arquitetura de preços',
    component: SetupIpaPriceArchitecture,
    isVisible: ({ contratosAdicionais }) => hasPriceArchitectureAccess(contratosAdicionais),
    productVisibility: { product: 'PRICING', additional: PRICE_ARCHITECTURE_CONTRACT },
  },
  'avisos-e-notificacoes': {
    to: 'avisos-e-notificacoes',
    label: 'Avisos e notificações',
    component: SetupIpaEngineUpdateNotifications,
    // Visível/editável só para o perfil Administrador (AC-F1). O aviso é da rodada do motor de
    // precificação: na regra por produto, só para quem tem IPA - Precificação.
    adminOnly: true,
    productVisibility: { product: 'PRICING' },
  },
};

const { to: DEFAULT_PATH } = MENU_ITEMS['dados-de-concorrencia'];

type SetupIPAProps = RouteComponentProps<Record<string, string>> &
  React.HtmlHTMLAttributes<HTMLDivElement> & {
    usuario?: {
      tipoAdicionalContratos?: {
        IPA?: string[];
      };
    };
  };

const SetupIPA = ({ className, match, ...props }: SetupIPAProps) => {
  const user = useSelector(selectorUser);
  const userLoadFinished = useSelector(selectorUserLoadFinished);
  const params = match.params?.[0];
  const isUserLoaded = user != null;
  const isWaitingForAccessRule = !hasResolvedAccessRule(user) && !userLoadFinished;
  const contratosAdicionais = useMemo(() => user?.tipoAdicionalContratos?.IPA ?? [], [user?.tipoAdicionalContratos?.IPA]);
  const visibilityContext = useMemo(() => {
    const { hasPricing, hasNegotiation, clientHasNegotiation } = getProductAccess(user);
    return {
      user,
      contratosAdicionais,
      productRule: isProductRule(user),
      hasPricing,
      hasNegotiation,
      clientHasNegotiation,
    };
  }, [contratosAdicionais, user]);

  const activePath = (params || DEFAULT_PATH) as keyof typeof MENU_ITEMS;

  const [selectedMenu, setSelectedMenu] = useState(activePath);

  const availableMenuItems = useMemo(() => Object.entries(MENU_ITEMS).filter(([_, item]) => isMenuItemVisible(item, visibilityContext)), [visibilityContext]);

  const fallbackPath = (availableMenuItems[0]?.[1].to ?? DEFAULT_PATH) as keyof typeof MENU_ITEMS;
  const requestedMenuItem = availableMenuItems.find(([key]) => key === activePath)?.[1];
  const hasRequestedMenuAccess = requestedMenuItem != null;
  const currentMenuItem = availableMenuItems.find(([key]) => key === selectedMenu)?.[1];

  useEffect(() => {
    if (isWaitingForAccessRule) return;
    if (!isUserLoaded && !hasRequestedMenuAccess) return;

    setSelectedMenu(hasRequestedMenuAccess ? activePath : fallbackPath);
  }, [activePath, fallbackPath, hasRequestedMenuAccess, isUserLoaded, isWaitingForAccessRule]);

  const MemoMenuItem = useMemo(() => {
    return currentMenuItem?.component ?? MENU_ITEMS[fallbackPath].component;
  }, [currentMenuItem, fallbackPath]);

  if (!params) {
    History.push(`${PREFIX_URL}/${DEFAULT_PATH}`);
    return null;
  }

  // Sem a regra resolvida nenhum submenu monta: algum pediria leitura que a regra por produto nega.
  if (isWaitingForAccessRule) {
    return null;
  }

  if (!isUserLoaded && !hasRequestedMenuAccess) {
    return null;
  }

  // Cliente sem nenhum produto IPA: sem submenu para onde ir, e redirecionar viraria laço.
  if (availableMenuItems.length === 0) {
    return null;
  }

  if (isUserLoaded && !hasRequestedMenuAccess) {
    History.push(`${PREFIX_URL}/${fallbackPath}`);
    return null;
  }

  if (!currentMenuItem) {
    return null;
  }

  return (
    <div className={classNames(styles['setup-ipa'], className)} {...props}>
      <SetupMenu className={styles['setup-ipa__setup-menu']}>
        {availableMenuItems.map(([key, { to, label }]) => (
          <SetupMenuItem key={key} to={`${PREFIX_URL}/${to}`} data-testid={`settings-submenu-${key}`}>
            {label}
          </SetupMenuItem>
        ))}
      </SetupMenu>

      <div>
        <MemoMenuItem />
        <LoadingSpinerArea area="setup-ipa" height="100%" size="sm" />
      </div>
    </div>
  );
};

export default withRouter(SetupIPA);
